<script setup lang="ts">
import { useGameUI } from "@/features/dream-home/store/gameUI";
import { useHouseScene } from "@/features/dream-home/store/houseScene";
import { isMobile } from "@/modules/common";
import { useLocalStorage } from "@vueuse/core";
import gsap from "gsap";
import Hls from "hls.js";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const houseSceneStore = useHouseScene();
const gameUIStore = useGameUI();

const radioAudioRef = ref<HTMLAudioElement>();
const hlsInstance = ref<Hls>();
const autoPlay = useLocalStorage("radio-auto-play", true);
const shouldAutoPlay = computed(() => autoPlay.value && gameUIStore.isFreeMode);

onMounted(() => {
  houseSceneStore.radio.ref = radioAudioRef.value!;
  const source = "https://radio.bingciyuan.top/live/playlist.m3u8";
  if (Hls.isSupported()) {
    hlsInstance.value = new Hls({
      maxBufferSize: isMobile() ? 5 * 1000 * 1000 : 30 * 1000 * 1000,
    });

    const hls = hlsInstance.value;

    // 设置加载状态
    houseSceneStore.radio.isLoading = true;

    // 监听各种加载状态
    hls.on(Hls.Events.MANIFEST_LOADING, () => {
      houseSceneStore.radio.isLoading = true;
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      houseSceneStore.radio.isLoading = false;
      if (shouldAutoPlay.value) {
        radioAudioRef.value?.play();
      }
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.details === "bufferStalledError") {
        console.log("Buffer stalled, waiting for more content...");

        // 设置加载状态
        houseSceneStore.radio.isLoading = true;
        houseSceneStore.radio.isPlaying = false;

        // 暂停播放，等待缓冲
        radioAudioRef.value?.pause();

        // 检查缓冲区恢复
        const checkBuffer = () => {
          if (radioAudioRef.value) {
            const buffered = radioAudioRef.value.buffered;
            const currentTime = radioAudioRef.value.currentTime;

            if (buffered.length > 0) {
              const bufferEnd = buffered.end(buffered.length - 1);
              const availableBuffer = bufferEnd - currentTime;
              // 等待至少有3秒缓冲再恢复播放
              if (availableBuffer >= 3) {
                houseSceneStore.radio.isLoading = false;
                radioAudioRef.value.play().then(() => {
                  houseSceneStore.radio.isPlaying = true;
                });
                return;
              }
            }

            // 如果缓冲还不够，继续等待
            setTimeout(checkBuffer, 500);
          }
        };

        // 开始检查缓冲恢复
        setTimeout(checkBuffer, 1000);
      }
    });

    hls.loadSource(source);
    hls.attachMedia(radioAudioRef.value!);
  }
  // 对于原生支持 HLS 的浏览器（如 Safari）使用原生播放
  else if (radioAudioRef.value?.canPlayType("application/vnd.apple.mpegurl")) {
    radioAudioRef.value!.src = source;
  }

  // 设置初始状态为隐藏
  if (containerRef.value) {
    gsap.set(containerRef.value, {
      y: 100,
    });
  }
});
const containerRef = ref<HTMLDivElement>();
const hideControl = () => {
  gsap.to(containerRef.value!, {
    y: 100,
    duration: 0.5,
    ease: "power2.inOut",
  });
};

const showControl = () => {
  gsap.to(containerRef.value!, {
    y: 0,
    duration: 0.5,
    ease: "power2.inOut",
  });
};

const playRadio = async () => {
  radioAudioRef.value?.play();
  autoPlay.value = true;
};

const pauseRadio = () => {
  radioAudioRef.value?.pause();
  autoPlay.value = false;
};

onBeforeUnmount(() => {
  radioAudioRef.value?.pause();

  // 清理 HLS 实例
  if (hlsInstance.value) {
    hlsInstance.value.destroy();
    hlsInstance.value = undefined;
  }
});

export interface RadioPlayerControlInstance {
  showControl: () => void;
  hideControl: () => void;
}

defineExpose({
  showControl,
  hideControl,
});
</script>
<template>
  <div
    ref="containerRef"
    class="radio-player-control absolute bottom-2 inset-x-0 flex justify-center items-center"
  >
    <div
      class="flex p-2 px-4 justify-center items-center gap-2 bg-base-100 rounded-full pointer-events-auto shadow-lg border"
    >
      <audio
        ref="radioAudioRef"
        class="hidden"
        :autoplay="shouldAutoPlay"
        @play="houseSceneStore.radio.isPlaying = true"
        @playing="houseSceneStore.radio.isPlaying = true"
        @pause="houseSceneStore.radio.isPlaying = false"
        @loadstart="houseSceneStore.radio.isLoading = true"
        @loadedmetadata="houseSceneStore.radio.isLoading = false"
      />
      <div class="w-[1.5rem] h-[1.5rem] relative">
        <div
          class="w-full h-full bg-cover relative -top-1"
          :style="{ backgroundImage: `url(/game/items/radio.png)` }"
        ></div>
      </div>
      <div class="divider divider-horizontal mx-0" />
      <button
        class="btn btn-circle btn-sm"
        :class="{ 'btn-primary': !houseSceneStore.radio.isPlaying }"
        @click="playRadio"
        v-if="
          !houseSceneStore.radio.isPlaying && !houseSceneStore.radio.isLoading
        "
      >
        <i class="iconify mingcute--play-fill" />
      </button>
      <button
        class="btn btn-circle btn-sm"
        :class="{ 'btn-primary': houseSceneStore.radio.isPlaying }"
        @click="pauseRadio"
        v-if="
          houseSceneStore.radio.isPlaying && !houseSceneStore.radio.isLoading
        "
      >
        <i class="iconify mingcute--pause-fill" />
      </button>
      <button
        class="btn btn-circle btn-sm"
        v-if="houseSceneStore.radio.isLoading"
        disabled
      >
        <i class="loading loading-sm" />
      </button>
      <div class="divider divider-horizontal mx-0" />
      <button class="btn btn-circle btn-sm" @click="hideControl">
        <i class="iconify mingcute--down-fill" />
      </button>
    </div>
  </div>
</template>
